import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { dataTeamServices } from '@/data/dataTeamServices';
import { roadmapTools, selfServiceTools } from '@/data/selfServiceTools';
import {
  ArrowRight,
  Map as MapIcon,
  Users,
  Wrench,
} from 'lucide-react';

const palette = {
  benzol: '#51BA6D',
  benzolBorder: '#CFE9D3',
  selfServiceBg: '#F6FBF4',
  antwerp: '#068AA1',
  dataServicesBg: '#E6F6F9',
  dataServicesBorder: '#9FD7E3',
  workBorder: '#F5F9ED',
  viewer: '#5B8DEF',
  viewerBorder: '#BFD3F7',
  viewerBg: '#F5F8FF',
};
export default function AllTools() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const servicePreview = dataTeamServices.slice(0, 3);

  const goTo = (path: string) => {
    if (
      path.endsWith('.html') ||
      path.startsWith('http') ||
      path === '/viewer' ||
      path.startsWith('/viewer/')
    ) {
      // External or cross-app navigation. /viewer/* is served by sb-viewer
      // (SvelteKit container) and needs a full page load to hand off routing.
      window.location.href = path;
    } else {
      navigate(path);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F4F7FC]">
      <Sidebar activeItem="dashboard" user={user} />

      <main id="main-content" className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-10 md:px-10">
          <header className="rounded-3xl bg-white p-8 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">
              Studio Bereikbaar
            </p>
            <h1 className="mt-3 text-3xl font-bold text-sb-black">
              Tools & Data Platform
              <span className="ml-3 inline-flex items-center rounded-full border border-[#9FD7E3] bg-[#E6F6F9] px-3 py-0.5 text-sm font-semibold text-[#0F6B7C]">V4</span>
            </h1>
            <p className="mt-2 text-base text-sb-grey">
              Self-service tools, maps & the data library, data team services and OGC API processes,
              all accessible from one place. Launch a tool, open a map or request a service.
            </p>
          </header>

          <div className="grid grid-cols-1 gap-6">
            {/* Self-Service Tools */}
            <section
              className="rounded-3xl p-6 md:p-8 shadow-sm"
              style={{ border: `1px solid ${palette.benzolBorder}`, backgroundColor: palette.selfServiceBg }}
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#3F8A52]">
                    <Wrench size={16} />
                    Self-Service Tools
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold text-sb-black">
                    Launch independently
                  </h2>
                  <p className="mt-1 text-sm text-sb-dark-grey">
                    Open the tool, follow the prompts, and export branded deliverables without data team help.
                  </p>
                </div>
                <Button
                  className="mt-2 w-full rounded-full text-white md:w-auto hover:opacity-90"
                  style={{ backgroundColor: palette.benzol }}
                  onClick={() => goTo('/tools')}
                >
                  Browse all self-service tools
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>

              <div className="mt-6 space-y-4">
                {selfServiceTools.map((tool) => (
                  <div
                    key={tool.id}
                    className="rounded-2xl bg-white p-4 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <Badge
                          className="rounded-full text-xs font-semibold uppercase"
                          style={{
                            backgroundColor: palette.selfServiceBg,
                            color: palette.benzol,
                            border: `1px solid ${palette.benzolBorder}`,
                          }}
                        >
                          Self-Service
                        </Badge>
                        {/* Problem hook (Hugo audit W2: bring SelfService 'Need to ...?' tagline to Dashboard) */}
                        <p className="mt-2 text-sm font-bold text-sb-black">
                          {tool.problem}
                        </p>
                        <h3 className="mt-1 text-lg font-semibold text-sb-black">
                          {tool.name}
                        </h3>
                        <p className="text-sm text-sb-dark-grey">
                          {tool.description}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 md:items-end">
                        <Button
                          variant="outline"
                          className="rounded-full hover:bg-[#EFF7EF]"
                          style={{
                            borderColor: palette.benzolBorder,
                            color: palette.benzol,
                          }}
                          onClick={() => goTo(tool.link)}
                        >
                          Launch Tool
                        </Button>
                        <p className="text-xs text-gray-500">Inputs + outputs controlled by you</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Kaarten & Bibliotheek — landing page with the GeoLibre projects, the OGC data catalog and sb-viewer */}
            <section
              className="rounded-3xl p-6 md:p-8 shadow-sm"
              style={{ border: `1px solid ${palette.viewerBorder}`, backgroundColor: palette.viewerBg }}
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: palette.viewer }}>
                    <MapIcon size={16} />
                    Kaarten & Bibliotheek
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold text-sb-black">
                    Map, data library & thematic viewers
                  </h2>
                  <p className="mt-1 text-sm text-sb-dark-grey">
                    The GeoLibre map projects, the SB data catalog and the thematic viewers with project galleries, in one place.
                  </p>
                </div>
                <Button
                  className="mt-2 w-full rounded-full text-white md:w-auto hover:opacity-90"
                  style={{ backgroundColor: palette.viewer }}
                  onClick={() => goTo('/kaarten')}
                >
                  Open Kaarten & Bibliotheek
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </section>

            {/* Data Team Services */}
            <section
              className="rounded-3xl p-6 md:p-8 shadow-sm"
              style={{ border: `1px solid ${palette.dataServicesBorder}`, backgroundColor: palette.dataServicesBg }}
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0F6B7C]">
                    <Users size={16} />
                    Data Team Services
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold text-sb-black">
                    Request custom analysis
                  </h2>
                  <p className="mt-1 text-sm text-sb-dark-grey">
                    You brief us, we process the data, and we deliver maps, dashboards, or GeoPackages with clear turnaround times.
                  </p>
                </div>
                <Button
                  className="mt-2 w-full rounded-full text-white md:w-auto hover:opacity-90"
                  style={{ backgroundColor: palette.antwerp }}
                  onClick={() => goTo('/services')}
                >
                  View all 5 services
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                {servicePreview.map((service) => (
                  <div
                    key={service.id}
                    className="rounded-2xl border border-white bg-white/85 p-5 shadow-sm backdrop-blur-sm"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#0F6B7C]">
                      {service.contactPerson}
                    </p>
                    <h3 className="mt-2 text-lg font-semibold text-sb-black">
                      {service.title}
                    </h3>
                    <p className="text-sm text-sb-dark-grey">{service.tagline}</p>
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <span className="font-medium text-[#0F6B7C]">{service.timeEstimate}</span>
                      <button
                        className="text-sm font-semibold text-[#0F6B7C] underline"
                        onClick={() => goTo('/services')}
                      >
                        Request service
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Roadmap */}
          {roadmapTools.length > 0 && (
          <section className="rounded-3xl border border-[#E4E7EC] bg-white p-6 md:p-8 shadow-sm">
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
                Work In Progress
              </p>
              <h2 className="text-2xl font-semibold text-sb-black">Legacy tools under team review</h2>
              <p className="text-sm text-sb-dark-grey">
                Coming soon tools follow the same structure once validated by the team.
              </p>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              {roadmapTools.map((tool) => (
                <div
                  key={tool.id}
                  className="rounded-2xl border border-dashed p-4"
                  style={{ borderColor: palette.workBorder }}
                >
                  <div className="flex items-center gap-3">
                    <tool.icon size={24} className="text-gray-500" />
                    <div>
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                        {tool.status === 'planned' ? 'Coming Soon' : 'In Development'}
                      </p>
                      <h3 className="text-lg font-semibold text-sb-black">{tool.name}</h3>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-sb-dark-grey">{tool.description}</p>
                  <p className="mt-1 text-xs uppercase tracking-wide text-gray-400">
                    Team validation in progress
                  </p>
                </div>
              ))}
            </div>
        </section>
          )}
        </div>
      </main>
    </div>
  );
}
